import { InfoCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Divider, Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import { useModalContext } from '@/components/Modal/Modal';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import Heading from '@/components/Typography/Heading';
import Typo, { Paragraph, Span } from '@/components/Typography/Typo';
import queryKeys from '@/configs/react-query-keys';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import service from '@/services';
import { modalStyle } from '@/styles/style';
import {
  type FEEDBACK_COLUMNS,
  FeedbackColumnNameMap,
} from '@/types/filterKeyConst';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

type Props = {
  taxonomyAlias: string;
};

const StaticTaxonomyModal = (props: Props) => {
  const { closeModal } = useModalContext();
  const { taxonomyAlias } = props;
  const { parseStructuredFilter } = useStructuredFilter();
  const [state, setState] = useState<{
    title: string;
    desc?: string;
    dateFrom?: string;
    filter?: ReactNode;
  }>({
    title: '',
  });

  const { data: taxonomyData } = useQuery({
    queryFn: () => service.taxonomy.fetchTaxonomyItem(taxonomyAlias),
    queryKey: [...queryKeys.taxonomy.TAXONOMY_ITEM, taxonomyAlias],
  });

  useEffect(() => {
    if (taxonomyData) {
      const sFilter = new SFilter(taxonomyData.filter);
      const dataFilterMap = sFilter.getDataFilterMap();
      const dataFilter = parseStructuredFilter(dataFilterMap);

      const dataFilterValue = dataFilter.map((filter, index) => {
        const columnName = FeedbackColumnNameMap.get(
          filter.columnKey as FEEDBACK_COLUMNS,
        );
        return (
          <Typo key={index}>
            <Typo>{columnName}</Typo>
            &nbsp;
            <Typo className="text-gray-600">
              {structuredFilterUtils.getOperatorTextMap({
                operator: filter.operator,
                isMultiValueField: true,
                hasMultiValues: filter.filterValues.length > 1,
              })}
            </Typo>
            &nbsp;
            <Typo>
              {filter.filterValues.map((v) => v.filterName).join(', ')}
            </Typo>
            <br />
          </Typo>
        );
      });

      const dateFilter = sFilter.getDateFilter();

      setState({
        title: taxonomyData.title,
        desc: taxonomyData.desc,
        dateFrom: dateFilter.start_date || undefined,
        filter: dataFilterValue,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxonomyData]);

  return (
    <Style>
      <Flex padding="32px 32px 24px 32px">
        <Heading type="h2">View details</Heading>
      </Flex>
      <Divider />
      <VFlex padding="24px 32px" g={20}>
        <FilterItemDisplay
          label="Title"
          value={<FilterValue>{state.title}</FilterValue>}
        />
        {state.desc && (
          <FilterItemDisplay
            label="Description"
            value={<FilterValue>{state.desc}</FilterValue>}
          />
        )}
        {state.dateFrom && (
          <FilterItemDisplay
            label="Date from"
            value={
              <FilterValue>
                {getTimezoneDayjs(state.dateFrom).format(DATE_FORMAT.readable)}
              </FilterValue>
            }
          />
        )}
        {state.filter && (
          <FilterItemDisplay
            label="Filter"
            value={<FilterValue>{state.filter}</FilterValue>}
          />
        )}
      </VFlex>
      <Divider />
      <Flex padding="24px 32px" j="flex-end">
        <Button variant="outlined" sizeType="s" onClick={closeModal}>
          Done
        </Button>
      </Flex>
    </Style>
  );
};

const Style = styled.div`
  ${modalStyle};
  width: 600px;
`;

const FilterItemDisplay = (props: {
  label: string;
  value: ReactNode;
  tooltip?: ReactNode;
}) => {
  return (
    <Flex g={4}>
      <FilterLabel>
        {props.label}
        {props.tooltip && (
          <TooltipV2 tooltipNode={props.tooltip}>
            <InfoCircleOutlined style={{ paddingLeft: 4 }} />
          </TooltipV2>
        )}
      </FilterLabel>
      {props.value}
    </Flex>
  );
};

const FilterLabel = styled(Span).attrs({
  size: 2,
})`
  color: ${(props) => props.theme.colors.gray600};
  width: 200px;
`;

const FilterValue = styled(Paragraph).attrs({
  size: 2,
})`
  flex: 1;
  color: ${(props) => props.theme.colors.gray800};
`;

export default StaticTaxonomyModal;
